// Reusable PDF preview modal for the /inventory Purchase Order and Pick
// Slip PDFs. Renders the pdf in an iframe (via a blob: URL so nothing
// touches the server) and gives the user a Download and a Full-screen
// button — that way admins can look at a PO / slip without spamming
// their Downloads folder.

import React, { useEffect, useMemo, useState } from "react";
import { Download, FileText, Loader2, Maximize2, X } from "lucide-react";
import { NAVY, TEAL } from "./constants";
import { buildPurchaseOrderPDF, buildPickSlipPDF } from "../../lib/inventoryPdf";

function build(kind, payload) {
  if (kind === "po")   return buildPurchaseOrderPDF(payload);
  if (kind === "slip") return buildPickSlipPDF(payload);
  return null;
}

export function InventoryPdfPreview({ open, kind, payload, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");

  // Rebuild the PDF each time the modal opens with a fresh payload. The
  // blob is disposed on unmount / next open to avoid leaks.
  useEffect(() => {
    if (!open || !kind || !payload) return;
    let url = "";
    try {
      const built = build(kind, payload);
      if (!built) {
        setError("Unknown document type");
        return;
      }
      const blob = built.doc.output("blob");
      url = URL.createObjectURL(blob);
      setBlobUrl(url);
      setFilename(built.filename);
      setError("");
    } catch (e) {
      setError(e?.message || "Preview failed");
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open, kind, payload]);

  const title = useMemo(() => (
    kind === "po"   ? "Purchase Order preview" :
    kind === "slip" ? "Stock Pick Slip preview" :
    "PDF preview"
  ), [kind]);

  const doDownload = () => {
    if (!blobUrl || !filename) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const doFullscreen = () => {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/60 p-3 sm:p-6"
      onClick={onClose}
      data-testid="inv-pdf-preview"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[92vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-slate-100 flex-wrap">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" /> Preview
            </p>
            <h3 className="text-base font-semibold truncate" style={{ color: NAVY }}>{title}</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={doFullscreen}
              disabled={!blobUrl}
              data-testid="inv-pdf-fullscreen-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
            >
              <Maximize2 className="h-3.5 w-3.5" /> Open in new tab
            </button>
            <button
              type="button"
              onClick={doDownload}
              disabled={!blobUrl}
              data-testid="inv-pdf-download-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50"
              style={{ background: TEAL }}
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="inv-pdf-close-btn"
              aria-label="Close preview"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-500 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body — iframe renders the pdf inline; browsers ship a
            built-in reader so we don't need any extra JS. */}
        <div className="flex-1 min-h-0 bg-slate-50">
          {error ? (
            <div className="p-8 text-sm text-red-600">{error}</div>
          ) : !blobUrl ? (
            <div className="p-16 flex flex-col items-center justify-center gap-2 text-slate-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: TEAL }} />
              Rendering preview…
            </div>
          ) : (
            <iframe
              key={blobUrl}
              src={blobUrl + "#toolbar=0&navpanes=0"}
              title={title}
              className="w-full h-[70vh] border-0 block bg-white"
              data-testid="inv-pdf-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}
